import { useEffect, useState } from "react";

import { usePageNameContext } from "../../contexts/PageTitleContext";
import useReports from "../../data/useReports";
import { IReport } from "../../models/report/Report";
import Papa from "papaparse";
import { useParams } from "react-router-dom";
import LoadingWheel from "../shared/LoadingWheel";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import Card from "../shared/Card/Card";
import DynamicTable from "./DynamicTable";
import useNotifications from "../notifications/useNotifications";
import formatDateTime from "../../utils/formatDateTime";
import { DateTime } from "luxon";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const MAX_DISPLAY_ROWS = 500;

export default function ViewReport() {
  const { setInfo } = usePageNameContext();
  const { reports, runReport } = useReports();
  const { reportId } = useParams();
  const { addNotification } = useNotifications();

  const [report, setReport] = useState<IReport | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  useEffect(() => {
    setInfo({
      name: `Reports`,
      desc: `View and download reports`,
    });
  }, [setInfo]);

  useEffect(() => {
    if (reportId && !reports.isLoading) {
      const foundReport = reports.data?.data.find((r) => r.id === reportId);
      setReport(foundReport || null);
    }
  }, [reportId, reports.data, reports.isLoading]);

  const handleRunReport = (report: IReport) => {
    if (!report) return;

    if (report.allowDates && (!startDate || !endDate)) {
      // toast.error("Please select a start and end date");
      addNotification({
        variant: "error",
        primaryText: "Please select a start and end date",
      });
      return;
    };

    runReport.mutate({ id: report.id, startDate, endDate }, {
      // ...searchHandlers,
      onSuccess: (data) => {
        setReportData(data.data);
        // searchHandlers.onSuccess();
      }
    });
  }

  const handleCsvDownload = (reportData: any) => {
    if (!reportData) return;

    const csv = Papa.unparse(reportData);

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `${report?.title}-${formatDateTime(new Date().toISOString(), DateTime.DATE_SHORT)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (reports.isLoading) return <LoadingWheel />

  if (report === null) return <div>Report not found</div>

  const totalRows = Array.isArray(reportData) ? reportData.length : 0;
  const isTruncated = totalRows > MAX_DISPLAY_ROWS;
  const displayData = isTruncated ? reportData.slice(0, MAX_DISPLAY_ROWS) : reportData;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">

        <div className="flex items-center gap-4">
          {report?.allowDates && (
            <div className="flex gap-x-4 pr-6">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Start Date
                </label>
                <input
                  aria-label="Date from"
                  type="date"
                  value={startDate || ""}
                  // max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">
                  End Date
                </label>
                <input
                  aria-label="Date to"
                  type="date"
                  value={endDate || ""}
                  // min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border"
                />
              </div>
            </div>
          )}
          <div className="mt-7">
            <ButtonInput label="Run Report" isLoading={runReport.isLoading} onClick={() => handleRunReport(report)} classes="" />
          </div>
        </div>

        <div className="flex items-center gap-4 mt-7">
          <ButtonInput label="Download" disabled={!reportData} onClick={() => handleCsvDownload(reportData)} classes="" />
        </div>

      </div>

      {isTruncated && (
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
          <span>
            Showing the first {MAX_DISPLAY_ROWS.toLocaleString()} of{" "}
            {totalRows.toLocaleString()} rows. Download the report to get all rows.
          </span>
        </div>
      )}

      <div>
        <Card>
          <DynamicTable data={displayData} />
        </Card>
      </div>
    </div>
  );
}